import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import MediaGuard from "@/components/MediaGuard";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import { Loader2 } from "lucide-react";

// Route-level code splitting for lower-frequency screens.
// Home + Login stay eagerly loaded for fastest first paint.
const Cart = lazy(() => import("@/pages/Cart"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const Orders = lazy(() => import("@/pages/Orders"));
const Account = lazy(() => import("@/pages/Account"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminGate = lazy(() => import("@/pages/AdminGate"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const StaticPage = lazy(() => import("@/pages/StaticPage"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory">
      <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
    </div>
  );
}

function App() {
  const ADMIN_LOGIN_PATH = process.env.REACT_APP_ADMIN_LOGIN_PATH || "/secure-admin-login";
  const ADMIN_DASHBOARD_PATH = process.env.REACT_APP_ADMIN_DASHBOARD_PATH || "/secure-admin";
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <MediaGuard />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path={ADMIN_LOGIN_PATH} element={<AdminLogin />} />
                <Route path="/info/:slug" element={<StaticPage />} />
                <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/order-success/:id" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminGate />} />
                <Route path={ADMIN_DASHBOARD_PATH} element={<ProtectedRoute superOnly><Admin /></ProtectedRoute>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster
            position="top-center"
            richColors
            toastOptions={{
              className: "font-sans",
              style: { fontFamily: "Work Sans, sans-serif", borderRadius: "9999px" },
            }}
          />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
